import React from "react";
import { BoxAd, InBodyAds } from "@bloomberg/media-ui-ads";
import { TerminalTout } from "@bloomberg/media-ui-core";
import { useRegUIClient } from "@foundation/reg-ui-client";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import typographyStyles from "@bloomberg/media-ui-tokens/css/typography.module.css";
import styles from "./styles.module.css";
import { usePageData } from "~context/PageContext";
import type { SectionFrontPage } from "~types/types";
import SectionFront from "~components/SectionFront";
import { TodayInMarkets } from "~components/TodayInMarkets";
import { WatchlistWrappedContainer } from "~components/WatchlistWrappedContainer";
import Newsletter from "~components/Newsletter";
import UserModulesTopicStories from "~components/TopicModule/UserModulesTopicStories";
import { SectionFrontNav } from "~components/SectionFront/SectionFrontNav";
import BenchmarkComparison from "~components/BenchmarkComparison";
import MarketsSummary from "~components/MarketsSummary";
import { ZoneModule } from "~components/GridPhoenix/ZoneModule";
import { GridPhoenixLeftyLayout } from "~components/GridPhoenix/GridPhoenixLeftyLayout";
import { LazyLoad } from "~components/LazyLoad";
import { MarketSummariesPlaceholder } from "~components/MarketsSummary/MarketSummariesPlaceholder";
import { NewsletterPlaceholder } from "~components/Newsletter/NewsletterPlaceholder";
import { UserTopicsStoriesPlaceholder } from "~components/TopicModule/UserTopicsStoriesPlaceholder";
import { DEFAULT_BENCHMARK_COMPARISONS } from "~constants";
import { SurveyCallToActionTout } from "~components/SurveyCallToActionTout";
import { useOnDesktop } from "~hooks/useScreenBreakpoints";
import { useLocale } from "~hooks/i18n/useLocale";
import { TranslatedMessageType } from "~utils/i18n/translate";
import { TranslatedMessage } from "~utils/i18n";

type SectionFrontLayoutProps = {
  sectionTitle: string;
  sectionFrontPage: SectionFrontPage;
};

const SECTION_FRONT_TABS_WITHOUT_TIM = new Set<string>([
  "consumer-interest-rates",
  "cross-rates",
  "fx-fixings",
  "bloomberg-fixed-income-indices",
]);

const SECTION_FRONT_WITHOUT_TIM = new Set<string>(["rates-bonds"]);

export const StocksSurveyToutContent = () => (
  <>
    <h2 className={typographyStyles.coreHeadlineSm}>
      <TranslatedMessage
        id="surveyMsgHeader"
        defaultMessage="We’re testing something new."
      />
    </h2>
    <p className={typographyStyles.coreShortTextMd}>
      <TranslatedMessage
        id="surveyMsgBody"
        defaultMessage="Share your thoughts with us."
      />
    </p>
  </>
);

const onSurveyClick = () => {
  trackClickEvent("survey", "survey_market_data_page", "click", "engagement");
};

function TerminalToutComponent() {
  return (
    <div className={styles.terminalToutContainer}>
      <TerminalTout className={styles.terminalTout} />
      <TerminalTout className={styles.mobileTerminalTout} centerAligned />
    </div>
  );
}

export function LeftRail(props: {
  sectionFrontName: string;
  sectionTitle: string;
  subsectionTitle: string | TranslatedMessageType;
  searchClientURL: string;
  activeTab: string;
  countryCode?: string;
  isJapanese: boolean;
}) {
  const { sectionFrontName, countryCode, isJapanese } = props;
  const { session } = useRegUIClient();
  const bconnectId = session?.userInfo?.userId ?? "";

  return (
    <div className={styles.stickyEnabled}>
      <ZoneModule>
        <BenchmarkComparison defaultTickers={DEFAULT_BENCHMARK_COMPARISONS} />
      </ZoneModule>
      <ZoneModule>
        <TerminalToutComponent />
      </ZoneModule>
      <ZoneModule>
        <BoxAd flexSizeAllowed />
      </ZoneModule>
      {!isJapanese && (
        <ZoneModule>
          <LazyLoad placeholder={<MarketSummariesPlaceholder />}>
            <MarketsSummary sectionFrontName={sectionFrontName} />
          </LazyLoad>
        </ZoneModule>
      )}
      {!isJapanese && (
        <ZoneModule>
          <SurveyCallToActionTout
            toutContent={<StocksSurveyToutContent />}
            href={`https://bmedia.iad1.qualtrics.com/jfe/form/SV_4UEuJ1Zl43gyt02?bconnectId=${bconnectId}`}
            onClick={onSurveyClick}
          />
        </ZoneModule>
      )}
      {!isJapanese && (
        <ZoneModule>
          <LazyLoad placeholder={<NewsletterPlaceholder />}>
            <Newsletter
              id="markets-daily"
              countryCode={countryCode as string}
              embedUrl="markets-daily"
            />
          </LazyLoad>
        </ZoneModule>
      )}
    </div>
  );
}

export function BottomRail(props: {
  sectionFrontName: string;
  sectionTitle: string;
  subsectionTitle: string | TranslatedMessageType;
  searchClientURL: string;
  activeTab: string;
  countryCode?: string;
  isJapanese: boolean;
}) {
  const { sectionFrontName, countryCode, isJapanese } = props;
  const { session } = useRegUIClient();
  const bconnectId = session?.userInfo?.userId ?? "";

  return (
    <>
      <ZoneModule>
        <BenchmarkComparison defaultTickers={DEFAULT_BENCHMARK_COMPARISONS} />
      </ZoneModule>
      <ZoneModule>
        <TerminalToutComponent />
      </ZoneModule>
      <ZoneModule>
        <InBodyAds positionIncrement={2} />
      </ZoneModule>
      {!isJapanese && (
        <ZoneModule>
          <LazyLoad placeholder={<MarketSummariesPlaceholder />}>
            <MarketsSummary sectionFrontName={sectionFrontName} />
          </LazyLoad>
        </ZoneModule>
      )}
      <ZoneModule>
        <SurveyCallToActionTout
          toutContent={<StocksSurveyToutContent />}
          href={`https://bmedia.iad1.qualtrics.com/jfe/form/SV_4UEuJ1Zl43gyt02?bconnectId=${bconnectId}`}
          onClick={onSurveyClick}
        />
      </ZoneModule>
      {!isJapanese && (
        <ZoneModule>
          <LazyLoad placeholder={<NewsletterPlaceholder />}>
            <Newsletter
              id="markets-daily"
              countryCode={countryCode as string}
              embedUrl="markets-daily"
            />
          </LazyLoad>
        </ZoneModule>
      )}
    </>
  );
}

export default function SectionFrontLayout(props: SectionFrontLayoutProps) {
  const { sectionTitle, sectionFrontPage } = props;
  const { sectionFrontTab, headerTabs } = sectionFrontPage;
  const { searchClientURL = "", countryCode } = usePageData();

  const onDesktop = useOnDesktop();

  const { isJapanese } = useLocale();

  const shouldRenderTodayInMarkets =
    !SECTION_FRONT_TABS_WITHOUT_TIM.has(sectionFrontTab.identifier) &&
    !SECTION_FRONT_WITHOUT_TIM.has(sectionFrontPage.sectionFrontName);

  const topContent = (
    <>
      <SectionFrontNav
        sectionTitle={sectionFrontPage.sectionFrontTab.subsectionTitle}
        activeTab={sectionFrontTab.identifier}
        headerTabs={headerTabs}
        sectionFrontName={sectionFrontPage.sectionFrontName}
      />
      {shouldRenderTodayInMarkets && (
        <TodayInMarkets
          sectionFrontTab={sectionFrontTab}
          sectionFrontName={sectionFrontPage.sectionFrontName}
        />
      )}
    </>
  );

  const leftRail = onDesktop && (
    <LeftRail
      sectionFrontName={sectionFrontPage.sectionFrontName}
      sectionTitle={sectionTitle}
      subsectionTitle={sectionFrontPage.sectionFrontTab.subsectionTitle}
      countryCode={countryCode}
      searchClientURL={searchClientURL}
      activeTab={sectionFrontTab.identifier}
      isJapanese={isJapanese}
    />
  );

  return (
    <GridPhoenixLeftyLayout
      className={styles.gridLayoutContainer}
      topRail={topContent}
      leftRail={leftRail}
    >
      <ZoneModule>
        <SectionFront sectionFrontPage={sectionFrontPage} />
      </ZoneModule>

      {!isJapanese && (
        <>
          <WatchlistWrappedContainer />
          <ZoneModule className={styles.topicModule}>
            <LazyLoad
              placeholder={<UserTopicsStoriesPlaceholder />}
              onEnterDelay={0}
            >
              <UserModulesTopicStories />
            </LazyLoad>
          </ZoneModule>
        </>
      )}

      {/*
        Render left nav items at the bottom of the center rail on smaller breakpoints.
        Avoid hiding elements using breakpoint-specific CSS classes.
        This prevents duplicate components, reduces DOM size, and avoids unnecessary API calls triggered by hidden components.
      */}
      {!onDesktop && (
        <BottomRail
          sectionFrontName={sectionFrontPage.sectionFrontName}
          sectionTitle={sectionTitle}
          subsectionTitle={sectionFrontPage.sectionFrontTab.subsectionTitle}
          countryCode={countryCode}
          searchClientURL={searchClientURL}
          activeTab={sectionFrontTab.identifier}
          isJapanese={isJapanese}
        />
      )}
    </GridPhoenixLeftyLayout>
  );
}
